import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ContactInfo } from '@/types/assessment';
import {
  calculateAssessmentOverallScore,
  getScoreLevel,
  getTrainingScoreLevel,
} from '@/utils/assessmentUtils';

interface UseAIAdviceParams {
  scores: Record<string, { score: number; maxScore: number }>;
  answers: any;
  contactInfo: ContactInfo;
  assessmentType?: 'ia-diagnostic' | 'training-transformation';
  existingAdvice?: string;
}

export const useAIAdvice = ({
  scores,
  answers,
  contactInfo,
  assessmentType = 'ia-diagnostic',
  existingAdvice,
}: UseAIAdviceParams) => {
  const [advice, setAdvice] = useState<string>(existingAdvice || '');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasGeneratedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Déclenche automatiquement la génération et le stockage du PDF.
  const triggerAutomaticPDFGeneration = async (adviceText: string, overallScore: number) => {
    try {
      const scoreLevel = assessmentType === 'training-transformation'
        ? getTrainingScoreLevel(overallScore)
        : getScoreLevel(overallScore);

      const hasRequiredContact =
        !!contactInfo?.firstName?.trim() &&
        !!contactInfo?.lastName?.trim() &&
        !!contactInfo?.email?.trim() &&
        !!contactInfo?.function?.trim() &&
        !!contactInfo?.company?.trim();

      if (!hasRequiredContact) {
        console.warn('[AUTO-PDF] Required contact information is incomplete');
        return;
      }

      const contactInfoPayload: ContactInfo = {
        firstName: contactInfo.firstName.trim(),
        lastName: contactInfo.lastName.trim(),
        email: contactInfo.email.trim(),
        function: contactInfo.function.trim(),
        company: contactInfo.company.trim(),
        phone: contactInfo.phone?.trim() || '',
        sector: contactInfo.sector || '',
        orgType: contactInfo.orgType,
        teamSize: contactInfo.teamSize,
        receiveReport: contactInfo.receiveReport !== false,
      };

      const pdfRequestData = {
        store: true,
        scores,
        contactInfo: contactInfoPayload,
        advice: adviceText,
        overallScore,
        scoreLevel,
        answers,
        contactEmail: contactInfo.email.trim(),
        assessmentType,
      };

      supabase.functions.invoke('generate-assessment-pdf', {
        body: pdfRequestData,
      }).then(({ data, error: pdfError }) => {
        if (pdfError) {
          console.error('[AUTO-PDF] PDF generation/storage failed');
        } else if (data?.stored) {
          console.log('[AUTO-PDF] PDF generated and stored');
        } else {
          console.warn('[AUTO-PDF] Unexpected PDF response');
        }
      }).catch(() => {
        console.error('[AUTO-PDF] PDF generation/storage request failed');
      });
    } catch {
      console.error('[AUTO-PDF] Unable to prepare automatic PDF generation');
    }
  };

  useEffect(() => {
    if (existingAdvice && existingAdvice.trim()) {
      setAdvice(existingAdvice);
      setIsLoading(false);
      hasGeneratedRef.current = true;
      return;
    }

    if (hasGeneratedRef.current) return;
    hasGeneratedRef.current = true;

    const generateAdvice = async () => {
      const canonicalOverallScore = calculateAssessmentOverallScore(scores, assessmentType);

      try {
        setIsLoading(true);
        setError(null);

        abortControllerRef.current = new AbortController();
        const timeoutId = setTimeout(() => {
          abortControllerRef.current?.abort();
        }, 60000);

        // L'Edge Function utilise les données nominatives uniquement pour la
        // notification interne du diagnostic. Le prompt envoyé au modèle IA
        // est construit côté serveur à partir du contexte métier et des réponses.
        const requestBody = {
          scores,
          answers,
          assessmentType,
          overallScore: canonicalOverallScore,
          company: contactInfo.company || '',
          sector: contactInfo.sector || '',
          contactFunction: contactInfo.function || '',
          firstName: contactInfo.firstName || '',
          lastName: contactInfo.lastName || '',
          email: contactInfo.email || '',
          orgType: contactInfo.orgType || '',
          teamSize: contactInfo.teamSize || '',
          accessToken: (contactInfo as any).accessToken ?? null,
        };

        const startTime = Date.now();
        const { data, error: functionError } = await supabase.functions.invoke(
          'generate-assessment-advice',
          { body: requestBody },
        );
        const duration = Date.now() - startTime;
        clearTimeout(timeoutId);

        console.log('[AI ADVICE] Response received', {
          durationMs: duration,
          success: Boolean(data?.success),
          hasAdvice: Boolean(data?.advice),
        });

        if (functionError) {
          console.error('[AI ADVICE] Edge Function returned an error');
          throw functionError;
        }

        if (data?.success && data?.advice) {
          setAdvice(data.advice);
          // Le score du PDF reste celui calculé par la même source que l'écran,
          // même si le service de conseil évolue indépendamment.
          triggerAutomaticPDFGeneration(data.advice, canonicalOverallScore);
        } else {
          console.error('[AI ADVICE] Invalid response received');
          throw new Error(data?.error || 'Erreur lors de la génération du conseil IA');
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          setError('Timeout de génération IA');
        } else {
          console.error('[AI ADVICE] Advice generation failed');
          setError(err instanceof Error ? err.message : 'Erreur inconnue');
        }

        const fallbackText = getFallbackAdvice(scores, contactInfo, assessmentType);
        setAdvice(fallbackText);
        triggerAutomaticPDFGeneration(fallbackText, canonicalOverallScore);
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    };

    generateAdvice();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return { advice, isLoading, error };
};

function getFallbackAdvice(
  scores: Record<string, { score: number; maxScore: number }>,
  contactInfo: ContactInfo,
  assessmentType: 'ia-diagnostic' | 'training-transformation',
): string {
  const overallScore = calculateAssessmentOverallScore(scores, assessmentType);
  const companyName = contactInfo.company || 'votre organisation';

  if (assessmentType === 'training-transformation') {
    if (overallScore >= 81) {
      return `<strong style="color: #D7398B;">${companyName}</strong> montre une excellente maturité IA Learning 🧠. Votre stratégie de formation est exemplaire et intègre parfaitement les technologies IA. Continuez à innover et partagez vos bonnes pratiques.<br><br><strong style="color: #D7398B;">Votre potentiel est exceptionnel, discutons de vos prochains défis ! Réservez un call stratégique 🚀</strong>`;
    } else if (overallScore >= 61) {
      return `<strong style="color: #D7398B;">${companyName}</strong> progresse très bien dans sa transformation Learning IA 🌟. Votre approche formation intègre déjà l'IA de manière structurée. Il reste quelques axes d'optimisation pour maximiser l'impact.<br><br><strong style="color: #D7398B;">Vous êtes sur la bonne voie, accélérons ensemble ! Réservez un call de débrief 🚀</strong>`;
    } else if (overallScore >= 41) {
      return `<strong style="color: #D7398B;">${companyName}</strong> développe sa stratégie Learning IA 🚀. Vos pratiques de formation évoluent et l'IA commence à être intégrée. Il est temps de structurer davantage cette approche pour amplifier les résultats.<br><br><strong style="color: #D7398B;">C'est le moment de passer à l'étape suivante ! Réservez un call stratégique 🚀</strong>`;
    } else if (overallScore >= 21) {
      return `<strong style="color: #D7398B;">${companyName}</strong> explore les possibilités de l'IA Learning 🔧. Vos premières initiatives montrent un potentiel intéressant. Il est crucial de structurer une véritable stratégie pour capitaliser sur ces bases.<br><br><strong style="color: #D7398B;">Transformons vos premières expériences en stratégie ! Réservez un call de conseil 🚀</strong>`;
    }
    return `<strong style="color: #D7398B;">${companyName}</strong> a un potentiel de développement IA Learning considérable 🌱. C'est le moment idéal pour bâtir une stratégie formation innovante et structurer votre approche de l'IA pédagogique.<br><br><strong style="color: #D7398B;">Construisons ensemble votre stratégie Learning IA ! Réservez un call de découverte 🚀</strong>`;
  }

  if (overallScore >= 81) {
    return `<strong style="color: #D7398B;">${companyName}</strong> montre une excellente maturité IA 🧠. Continuez à innover et partagez vos bonnes pratiques. Les domaines les plus prometteurs pour votre développement futur sont ceux où vous pouvez encore progresser.<br><br><strong style="color: #D7398B;">Vous êtes sur la bonne voie, c'est le moment de passer à l'action ! Réservez un call de débrief avec notre expert 🚀</strong>`;
  } else if (overallScore >= 61) {
    return `<strong style="color: #D7398B;">${companyName}</strong> est sur la bonne voie 🌟. Concentrez-vous sur les domaines à améliorer pour accélérer votre transformation IA. Votre potentiel est réel et les opportunités nombreuses.<br><br><strong style="color: #D7398B;">Vous êtes sur la bonne voie, c'est le moment de passer à l'action ! Réservez un call de débrief avec notre expert 🚀</strong>`;
  } else if (overallScore >= 41) {
    return `<strong style="color: #D7398B;">${companyName}</strong> progresse dans sa transformation IA 🚀. Il est temps de structurer votre approche et d'accélérer sur les axes prioritaires. Chaque étape vous rapproche de vos objectifs.<br><br><strong style="color: #D7398B;">Vous êtes sur la bonne voie, c'est le moment de passer à l'action ! Réservez un call de débrief avec notre expert 🚀</strong>`;
  } else if (overallScore >= 21) {
    return `<strong style="color: #D7398B;">${companyName}</strong> entame sa transformation IA 🔧. Il est essentiel de poser les bonnes fondations et de former vos équipes. Votre potentiel de développement est considérable.<br><br><strong style="color: #D7398B;">Vous êtes sur la bonne voie, c'est le moment de passer à l'action ! Réservez un call de débrief avec notre expert 🚀</strong>`;
  }
  return `<strong style="color: #D7398B;">${companyName}</strong> a un fort potentiel de développement IA 🌱. Commencez par structurer votre vision et former vos équipes aux enjeux IA. Chaque étape vous rapprochera de vos objectifs.<br><br><strong style="color: #D7398B;">Vous êtes sur la bonne voie, c'est le moment de passer à l'action ! Réservez un call de débrief avec notre expert 🚀</strong>`;
}
